// src/api/knowledge.js
//
// Thin client for the proxy's knowledge-freshness REST endpoints (WP7,
// docs/launch-signoff-spec.md §9 — proxy-server/apiServer.js "knowledge"
// block, backed by proxy-server/knowledge/freshness.js). Mirrors the private
// request() helper in api/checks.js / api/launches.js — same base-URL
// resolution (REACT_APP_PROXY_API_URL in prod, falls back to a bare "/proxy"
// path relative to the page origin locally).
//
// NOTE: this is distinct from `getKnowledgePacks()` in api/checks.js, which
// hits plain `GET /proxy/knowledge` (the packs themselves, no freshness
// math) and is what frontend/src/knowledge/index.js still uses to populate
// KNOWLEDGE_BASE. This module is only the freshness/refresh/proposal layer
// consumed by pages/KnowledgePage.jsx.
//
//   GET  /proxy/knowledge/status  -> { packs: [{ id, name, version,
//                                     lastUpdated, lastVerified, staleDays,
//                                     stale, overridden, sourceUrls }] }
//                                     (staleDays from lastVerified ?? lastUpdated;
//                                     threshold env KNOWLEDGE_STALE_DAYS, default
//                                     60). Deterministic, no LLM call, never
//                                     throws server-side — always 200.
//   POST /proxy/knowledge/refresh { packId }
//                                  -> 200 { ok:true, proposal }
//                                     400 { ok:false, issues:[...], fetchNotes? }
//                                     404 { error:'pack_not_found' }
//                                     409 { error:'anthropic_api_key_missing', detail }
//                                  Fetches the pack's own sourceUrls (<=5,
//                                  <=200KB each) and makes ONE Sonnet call
//                                  proposing source-attributable changes.
//   GET  /proxy/knowledge/proposals -> { proposals: [{ packId, ts, summary,
//                                     noChanges?, changes:[{kind, requirementId?,
//                                     before?, after?, sourceUrl}], fetchNotes }] }
//                                     All pending proposals. Never throws — 200 always.
//   POST /proxy/knowledge/proposals/<packId>/approve
//                                  -> 200 { ok:true, pack, issues? } (issues here
//                                     are non-fatal per-change skip notes, not a
//                                     failure)
//                                     400 { ok:false, issues:[...] }
//                                     404 { error:'pack_not_found'|'proposal_not_found' }
//                                  Applies the pending proposal's changes,
//                                  bumps the pack's patch version, stamps
//                                  lastUpdated+lastVerified, writes the proxy
//                                  overlay, deletes the proposal, reloads.
//   POST /proxy/knowledge/proposals/<packId>/dismiss
//                                  -> 200 { ok:true, pack }
//                                     400 { ok:false, issues:[...] }
//                                     404 { error:'pack_not_found' }
//                                  Stamps lastVerified=today only — no
//                                  requirement/version changes.
//
// Unlike compileLaunch() in api/launches.js (whose { ok:false } shape
// resolves at HTTP 200), refresh/approve/dismiss all send their { ok:false }
// body at HTTP 400 — so ALL non-2xx outcomes here (400 issues, 404 not-found,
// 409 missing key) reject the same way, with `.status` and `.body` on the
// thrown Error. Callers branch on `err.status`/`err.body` in one catch block.
//
// Response shapes are echoed back to the caller as-is (parsed JSON) — the
// page does its own defensive optional-chaining on the fields it reads.

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: POST/GET JSON against the proxy REST API. Always resolves the
 * parsed JSON body on 2xx. On a non-2xx response, rejects with an Error
 * whose `.status` and `.body` carry the server's status code and parsed
 * error body, so callers can branch on the specific error code rather than
 * just a message. Mirrors api/checks.js's / api/launches.js's request()
 * exactly.
 */
async function request(path, init = {}) {
  let res;
  try {
    // Routes live under /proxy/knowledge/* on the proxy REST API.
    // PROXY_API_BASE is the host (no /proxy), so prepend it here — matches
    // api/checks.js and api/launches.js.
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/knowledge/status -> [{ id, name, version, lastUpdated,
 * lastVerified, staleDays, stale, overridden, sourceUrls }].
 *
 * Unlike getKnowledgePacks() in api/checks.js (which catches-and-degrades to
 * [] so a background list fetch can fail silently), this one does NOT
 * swallow errors: a "proxy unreachable" rejection here (err.status === 0)
 * needs to reach the page so it can render the "can't reach the proxy" banner
 * per docs/launch-signoff-spec.md §9, rather than being indistinguishable
 * from "0 packs configured". Callers should wrap in try/catch.
 */
export async function knowledgeStatus() {
  const data = await request('/knowledge/status', { method: 'GET' });
  return Array.isArray(data?.packs) ? data.packs : [];
}

/**
 * POST /proxy/knowledge/refresh { packId } -> { ok:true, proposal }.
 * Rejects (does not resolve an { ok:false } shape) on any non-2xx:
 *   - 400 { ok:false, issues:[...], fetchNotes? } (e.g. no_source_docs_fetched,
 *     malformed LLM output)
 *   - 404 { error:'pack_not_found' }
 *   - 409 { error:'anthropic_api_key_missing', detail } (proxy needs a key)
 * Callers should wrap in try/catch and branch on `err.status`/`err.body`.
 */
export async function refreshKnowledgePack(packId) {
  return request('/knowledge/refresh', {
    method: 'POST',
    body: JSON.stringify({ packId }),
  });
}

/**
 * GET /proxy/knowledge/proposals -> [{ packId, ts, summary, noChanges?,
 * changes:[...], fetchNotes }]. Same "does not swallow errors" contract as
 * knowledgeStatus() above — callers should wrap in try/catch.
 */
export async function listKnowledgeProposals() {
  const data = await request('/knowledge/proposals', { method: 'GET' });
  return Array.isArray(data?.proposals) ? data.proposals : [];
}

/**
 * POST /proxy/knowledge/proposals/<packId>/approve -> { ok:true, pack, issues? }.
 * Rejects on 400 { ok:false, issues:[...] } (resulting pack failed structural
 * validation — nothing was written, the proposal is kept for retry/dismiss)
 * or 404 { error:'pack_not_found'|'proposal_not_found' }.
 */
export async function approveKnowledgeProposal(packId) {
  return request(`/knowledge/proposals/${encodeURIComponent(packId)}/approve`, {
    method: 'POST',
  });
}

/**
 * POST /proxy/knowledge/proposals/<packId>/dismiss -> { ok:true, pack }.
 * Stamps lastVerified=today and drops the pending proposal WITHOUT applying
 * its changes. Rejects on 400 { ok:false, issues:[...] } or 404
 * { error:'pack_not_found' }.
 */
export async function dismissKnowledgeProposal(packId) {
  return request(`/knowledge/proposals/${encodeURIComponent(packId)}/dismiss`, {
    method: 'POST',
  });
}
